import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLazyQuery, useMutation as useApolloMutation } from "@apollo/client";
import { PROPERTY_DETAILS, SUBMIT_ENQUIRY, REQUEST_FOR_APPROVAL } from "@/Graphql/Queries";
import client from "@/Graphql/apolloClient";
import { authStorage } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import {
  Sparkles,
  ArrowLeft,
  CheckCircle,
  XCircle,
  MapPin,
  Monitor,
  Eye,
  Loader2,
  Download,
  Calendar,
  DollarSign,
  Building2,
  FileText,
  Send
} from "lucide-react";
import jsPDF from "jspdf";
import logger from '@/utils/logger';
import { calculateSpotsPerDay, calculateTotalRunningHours } from '@/utils/spotCalculations';

interface ScreenItem {
  id?: string; // Full ID from chat response
  inventoryId: number;
  displayName: string;
  assetUrl?: string;
  location: string;
  venueType?: string;
  screenType?: string;
  size?: string;
  resolution?: string;
  price: number;
  impressions?: number;
  spots?: number;
  days?: number;
  hours?: number;
  // Enhanced fields from Billboard_DETAILS
  referenceId?: string;
  company?: string;
  category?: string;
  subCategory?: string;
  latitude?: number;
  longitude?: number;
  availableBooking?: boolean;
  active?: boolean;
  totalVisitors?: number;
  resolution1Width?: string;
  resolution1Height?: string;
  spotDuration?: number;
  perDayPrice?: number;
  sellingRate?: {
    daily?: number;
    weekly?: number;
    monthly?: {
      month1?: number;
      month3?: number;
      month6?: number;
      month12?: number;
    };
  };
  currency?: {
    code: string;
    symbol: string;
    country: string;
  };
  countryId?: string;
  isLoading?: boolean;
  hasError?: boolean;
  // Spot calculation fields
  spotsPerHour?: number;
  calculatedSpotsPerDay?: number;
  displayOnTime?: string;
  displayOffTime?: string;
  screensCount?: number;
  calculatedHoursPerDay?: number;
}

interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
}

interface PackageData {
  packageId: string;
  sessionId?: string;
  screens: ScreenItem[];
  totalScreens: number;
  budget: number;
  totalImpressions: number;
  totalReach: number;
  totalSpots: number;
  avgFrequency: number;
  location: string;
  country: string;
  countryId?: string;
  startDate: string;
  endDate: string;
  duration: string;
  businessType?: string;
  status?: string;
  currency?: CurrencyInfo;
  totalDays?: number;
  hoursPerDay?: number;
}

interface LeadFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  address: string;
}

function MetricBox({ label, value, icon: Icon }: { label: string; value: string | number; icon?: any }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3 text-center">
      {Icon && <Icon className="h-4 w-4 mx-auto mb-1 text-emerald-600" />}
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-bold text-lg text-gray-900">{value}</div>
    </div>
  );
}

function formatResolution(resolution?: string): string {
  if (!resolution) return "";
  const match = resolution.match(/(\d+)\s*[xX×]\s*(\d+)/);
  if (match) return `${match[1]} X ${match[2]}`;
  return resolution;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  const year = date.getFullYear();
  const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
  return `${day}${suffix} ${month} ${year}`;
}

function ItemCard({ item, index, startDate, endDate, totalDays = 1 }: { item: ScreenItem; index: number; startDate?: string; endDate?: string; totalDays?: number }) {
  const isClassic = item.screenType?.toLowerCase() === 'classic' || item.screenType?.toLowerCase() === 'static';
  // Use calculated spots per day if available (from spotsPerHour calculation), otherwise fall back to existing logic
  const spotsPerDay = isClassic && !item.spots && !item.calculatedSpotsPerDay 
    ? 'N/A' 
    : item.calculatedSpotsPerDay 
      ? Math.round(item.calculatedSpotsPerDay)
      : (item.days && item.spots ? Math.round(item.spots / item.days) : item.spots || 0);
  const totalSpots = item.spots || 0;
  // Calculate impressions: (impressions/30) * total days
  const totalImpressions = Math.round((item.impressions || 0) / 30 * totalDays);
  const isDigital = item.screenType?.toLowerCase() === 'digital';
  
  return (
    <div className="border rounded-lg overflow-hidden hover:shadow-md transition-all bg-white relative">
      {item.isLoading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        </div>
      )}
      
      <div className="aspect-video relative bg-gray-100">
        {item.assetUrl ? (
          <img
            src={item.assetUrl}
            alt={item.displayName || "Billboard"}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <Monitor className="h-12 w-12" />
          </div>
        )}
        {item.screenType && (
          <Badge className="absolute top-2 left-2 bg-emerald-600 text-white text-xs">
            {item.screenType}
          </Badge>
        )}
        {item.hasError && (
          <Badge className="absolute top-2 right-2 bg-yellow-500 text-white text-xs">
            Limited Data
          </Badge>
        )}
      </div>
      <div className="p-3">
        <h4 className="font-semibold text-sm truncate">{item.displayName}</h4>
        {item.referenceId && (
          <p className="text-xs text-gray-500 mb-1">ID: {item.referenceId}</p>
        )}
        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
          <MapPin className="h-3 w-3" />
          <span className="truncate">{item.location}</span>
        </div>
        {item.company && (
          <p className="text-xs text-emerald-600 mt-1 font-medium">{item.company}</p>
        )}
        <div className="flex flex-wrap gap-1 mt-2">
          {item.venueType && (
            <Badge variant="outline" className="text-xs">
              {item.venueType}
            </Badge>
          )}
          {(item.resolution || item.size) && (
            <Badge className="text-xs bg-emerald-600 text-white">
              {formatResolution(item.resolution || item.size)}
            </Badge>
          )}
          {item.category && (
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
              {item.category}
            </Badge>
          )}
        </div>
        
        <div className="mt-3 pt-3 border-t text-xs text-gray-700 space-y-1">
          {isDigital && (
            <div className="flex justify-between">
              <span className="text-gray-500">Spots/Day</span>
              <span className="font-medium">{typeof spotsPerDay === 'string' ? spotsPerDay : spotsPerDay.toLocaleString()}</span>
            </div>
          )}
          {item.spotDuration && (
            <div className="flex justify-between">
              <span className="text-gray-500">Spot Duration</span>
              <span className="font-medium">{item.spotDuration}s</span>
            </div>
          )}
          {!isClassic && (item.calculatedHoursPerDay || item.hours) && (
            <div className="flex justify-between">
              <span className="text-gray-500">Hours/Day</span>
              <span className="font-medium">{item.calculatedHoursPerDay ? Math.round(item.calculatedHoursPerDay * 10) / 10 : item.hours} Hr</span>
            </div>
          )}
          {startDate && (
            <div className="flex justify-between">
              <span className="text-gray-500">Starts</span>
              <span className="font-medium">{formatDate(startDate)}</span>
            </div>
          )}
          {endDate && (
            <div className="flex justify-between">
              <span className="text-gray-500">Ends</span>
              <span className="font-medium">{formatDate(endDate)}</span>
            </div>
          )}
          <div className="pt-2 border-t mt-2">
            {!(isClassic || isDigital) && (
              <div className="flex justify-between text-emerald-700">
                <span>Total Spots</span>
                <span className="font-bold">{totalSpots.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-emerald-700">
              <span>Impressions</span>
              <span className="font-bold">{(item.impressions || 0).toLocaleString()} / Day</span>
            </div>
            {item.perDayPrice && (
              <div className="flex justify-between text-blue-700 mt-1 font-semibold text-sm">
                <span>Price</span>
                <span className="font-bold">{item.currency?.code || 'N/A'} {item.perDayPrice.toLocaleString()} / Day</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AIPackageReviewPage() {
  const navigate = useNavigate();
  const { packageId } = useParams<{ packageId: string }>();
  const { toast } = useToast();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadForm, setLeadForm] = useState<LeadFormData>({
    name: "",
    email: "",
    phone: "",
    company: "",
    address: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingProposal, setIsCreatingProposal] = useState(false);
  const [enhancedScreens, setEnhancedScreens] = useState<ScreenItem[]>([]);
  const [fetchPropertyDetails] = useLazyQuery(PROPERTY_DETAILS);
  const [submitEnquiry] = useApolloMutation(SUBMIT_ENQUIRY);

  const { data, isLoading, error } = useQuery({
    queryKey: ["/api/ai/package", packageId],
    queryFn: async () => {
      const stored = sessionStorage.getItem("pendingPackage");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.packageId === packageId) {
          return { success: true, packageData: parsed };
        }
      }
      const response = await fetch(`/api/ai/package/${packageId}`);
      return response.json();
    },
    enabled: !!packageId
  });

  const packageData: PackageData | null = data?.packageData || null;

  // Debug the date values
  useEffect(() => {
    if (packageData) {
      logger.log('Package Data Debug:', {
        startDate: packageData.startDate,
        endDate: packageData.endDate,
        totalDays: packageData.totalDays,
        budget: packageData.budget,
        screens: packageData.totalScreens
      });
    }
  }, [packageData]);

  // Function to map billboard type from chat response to GraphQL enum
  const getBillboardTypeEnum = (type: string): string => {
    switch (type?.toLowerCase()) {
      case 'digital':
        return 'DIGITAL';
      case 'static':
      case 'classic':
        return 'CLASSIC';
      default:
        return 'DIGITAL';
    }
  };

  // Function to fetch enhanced billboard details
  const fetchEnhancedBillboardData = async (screens: ScreenItem[]) => {
    const enhancedData: ScreenItem[] = [];
    
    // Initialize with loading states
    const initialScreens = screens.map(screen => ({ 
      ...screen, 
      isLoading: true, 
      hasError: false 
    }));
    setEnhancedScreens(initialScreens);

    // Fetch details for each billboard
    for (let i = 0; i < screens.length; i++) {
      const screen = screens[i];
      try {
        logger.log(`Fetching details for billboard ${i + 1}/${screens.length}:`, {
          id: screen.id,
          inventoryId: screen.inventoryId,
          type: screen.screenType,
          billboardType: getBillboardTypeEnum(screen.screenType || '')
        });
        
        const result = await fetchPropertyDetails({
          variables: {
            id: screen.id || screen.inventoryId.toString(),
            billboardType: getBillboardTypeEnum(screen.screenType || ''),
          }
        });
        
        // Check for GraphQL errors
        if (result.error) {
          logger.error(`GraphQL error for billboard ${screen.inventoryId}:`, result.error);
          enhancedData.push({ ...screen, isLoading: false, hasError: true });
          setEnhancedScreens([...enhancedData, ...initialScreens.slice(enhancedData.length)]);
          continue;
        }
        
        const propertyData = result.data;
        
        logger.log(`Billboard ${i + 1} response:`, {
          hasData: !!propertyData,
          hasPropertyDetails: !!propertyData?.propertyDetails,
          error: result.error
        });

        if (propertyData?.propertyDetails) {
          const details = propertyData.propertyDetails;
          
          // Calculate impressions from API data if available
          const impressionsPerDay = details.impressionPerDay || screen.impressions || 0;
          const impressionsPerSpot = details.impressionPerSpot || 0;
          
          // Get panel size from specification
          const panelWidth = details.specification?.panel1Width || details.specification?.panelWidth;
          const panelHeight = details.specification?.panel1Height || details.specification?.panelHeight;
          const panelSize = details.specification?.panel1Size || details.specification?.billboardSize;
          
          // Calculate spots per day and hours per day for digital billboards
          const spotsPerHour = details.spotsPerHour ? parseFloat(details.spotsPerHour) : null;
          const displayOnTime = details.price?.displayOnTime || '';
          const displayOffTime = details.price?.displayOffTime || '';
          const screensCount = details.specification?.screens ? parseFloat(details.specification.screens) : 1;
          
          const calculatedSpotsPerDay = (spotsPerHour && displayOnTime && displayOffTime)
            ? calculateSpotsPerDay(spotsPerHour, displayOnTime, displayOffTime, screensCount)
            : null;
          
          const calculatedHoursPerDay = (displayOnTime && displayOffTime)
            ? calculateTotalRunningHours(displayOnTime, displayOffTime)
            : null;
          
          const enhancedScreen: ScreenItem = {
            ...screen,
            // Enhanced data from API
            referenceId: details.referenceId,
            displayName: details.displayName || details.name,
            company: details.company,
            category: details.category,
            subCategory: details.subCategory,
            latitude: details.latitude,
            longitude: details.longitude,
            venueType: details.venueType,
            availableBooking: details.availableBooking,
            active: details.active,
            totalVisitors: details.totalVisitors,
            resolution1Width: details.specification?.resolution1Width,
            resolution1Height: details.specification?.resolution1Height,
            resolution: details.specification?.resolution1Width && details.specification?.resolution1Height 
              ? `${details.specification.resolution1Width} X ${details.specification.resolution1Height}`
              : screen.resolution,
            size: panelSize || (panelWidth && panelHeight ? `${panelWidth} X ${panelHeight}` : screen.size),
            spotDuration: details.price?.spotDuration,
            perDayPrice: details.perDayPrice,
            price: details.perDayPrice || screen.price,
            sellingRate: details.sellingRate,
            currency: details.price?.localCurrencies?.code ? {
              code: details.price.localCurrencies.code,
              symbol: details.price.localCurrencies.symbol || details.price.localCurrencies.code,
              country: details.price.localCurrencies.country || ''
            } : screen.currency,
            assetUrl: details.thumbnailPath || screen.assetUrl,
            location: details.formattedAddress || screen.location,
            impressions: impressionsPerDay || screen.impressions,
            screenType: details.type || screen.screenType,
            countryId: details.country?.id,
            isLoading: false,
            hasError: false,
            // Spot calculation fields
            spotsPerHour: spotsPerHour || undefined,
            calculatedSpotsPerDay: calculatedSpotsPerDay || undefined,
            displayOnTime: displayOnTime || undefined,
            displayOffTime: displayOffTime || undefined,
            screensCount: screensCount,
            calculatedHoursPerDay: calculatedHoursPerDay || undefined
          };
          enhancedData.push(enhancedScreen);
        } else {
          // If API call succeeds but no data, keep original with error flag
          enhancedData.push({ ...screen, isLoading: false, hasError: true });
        }
      } catch (error) {
        logger.error(`Failed to fetch details for billboard ${screen.inventoryId}:`, error);
        // Keep original screen data but mark as error
        enhancedData.push({ ...screen, isLoading: false, hasError: true });
      }

      // Update state with current progress
      setEnhancedScreens([...enhancedData, ...initialScreens.slice(enhancedData.length)]);
    }

    setEnhancedScreens(enhancedData);
  };

  // Fetch enhanced data when package data is available
  useEffect(() => {
    if (packageData?.screens && packageData.screens.length > 0) {
      fetchEnhancedBillboardData(packageData.screens);
    }
  }, [packageData]);

  const loadImageAsBase64 = (url: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  const handleDownloadPDF = async () => {
    if (!packageData) return;
    
    toast({
      title: "Generating PDF...",
      description: "Please wait while we prepare your campaign summary."
    });

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = 0;
      
      const currencyCode = (enhancedScreens && enhancedScreens.length > 0 && enhancedScreens[0]?.currency?.code) || packageData.currency?.code || 'INR';
      const formatCurrency = (amount: number) => `${currencyCode} ${amount.toLocaleString()}`;
      const generatedDate = new Date().toLocaleString();

      // Green Header Background (matching payment invoice style)
      doc.setFillColor(5, 150, 105);
      doc.rect(0, 0, pageWidth, 50, "F");
      
      // Header - Logo and Company Details (Left Side)
      let logoDrawn = false;
      try {
        // Try to load logo from window.logoPath, footerConfig, or public assets
        let logoPath = typeof window !== "undefined" ? (window as any).logoPath : "";
        
        // If no logoPath, try to get from footerConfig
        if (!logoPath && typeof window !== "undefined" && (window as any).footerConfig) {
          logoPath = (window as any).footerConfig.logoPath || "";
        }
        
        // If still no logoPath, use default from public folder
        if (!logoPath) {
          logoPath = "/attached_assets/MW-Logo_1755605276936.png";
        }
        
        if (logoPath) {
          const img = new window.Image();
          img.crossOrigin = "anonymous";
          img.src = logoPath;
          // Wait for image to load
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          });
          // Draw logo (width: 35, height: 18)
          doc.addImage(img, "PNG", 10, 7, 35, 18);
          logoDrawn = true;
        }
      } catch (e) {
        // Fallback if logo fails to load
        logger.error("Logo loading error:", e);
        logoDrawn = false;
      }

      // Company Details on Left Side
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      
      // Get company details from window.footerConfig if available
      let companyUEN = "UEN ID: 201306382N";
      let addressLine1 = "";
      let addressLine2 = "";
      
      if (typeof window !== "undefined" && (window as any).footerConfig) {
        const config = (window as any).footerConfig;
        if (config.uen) companyUEN = `UEN ID: ${config.uen}`;
        if (config.address) {
          const addressParts = config.address.split(",").map((part: string) => part.trim());
          addressLine1 = (addressParts[0] || "") + (addressParts[1] ? " " + addressParts[1] : "");
          addressLine2 = addressParts[2] || "";
        }
      }
      
      // Company Name on Right Side
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Moving Walls Pte Ltd", pageWidth - margin, 18, { align: "right" });
      
      // Right side details
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(companyUEN, pageWidth - margin, 26, { align: "right" });
      doc.text(addressLine1, pageWidth - margin, 32, { align: "right" });
      doc.text(addressLine2, pageWidth - margin, 37, { align: "right" });
      
      // Generated Date
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.text(`Generated: ${generatedDate}`, pageWidth - margin, 43, { align: "right" });
      
      yPos = 55;

      // Campaign Details Section
      doc.setFillColor(249, 250, 251);
      doc.setDrawColor(229, 231, 235);
      doc.roundedRect(margin, yPos, pageWidth - margin * 2, 42, 3, 3, "FD");
      
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(17, 24, 39);
      doc.text("Campaign Details", margin + 8, yPos + 10);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(75, 85, 99);
      
      const col1 = margin + 8;
      const col2 = margin + 95;
      
      doc.setFont("helvetica", "bold");
      doc.text("Location:", col1, yPos + 20);
      doc.setFont("helvetica", "normal");
      
      // Wrap location text if it's too long
      const locationText = `${packageData.location || "N/A"}, ${packageData.country || "N/A"}`;
      const maxLocationWidth = 62; // Width available before col2 starts
      const wrappedLocation = doc.splitTextToSize(locationText, maxLocationWidth);
      
      // Limit to 2 lines max
      if (wrappedLocation.length > 2) {
        doc.text(wrappedLocation[0], col1 + 25, yPos + 20);
        // Truncate second line and add ellipsis
        const secondLine = wrappedLocation[1];
        const truncated = secondLine.length > 30 ? secondLine.substring(0, 30) + "..." : secondLine;
        doc.text(truncated, col1 + 25, yPos + 24);
      } else if (wrappedLocation.length === 2) {
        doc.text(wrappedLocation[0], col1 + 25, yPos + 20);
        doc.text(wrappedLocation[1], col1 + 25, yPos + 24);
      } else {
        doc.text(wrappedLocation[0], col1 + 25, yPos + 20);
      }
      
      doc.setFont("helvetica", "bold");
      doc.text("Business Type:", col2, yPos + 20);
      doc.setFont("helvetica", "normal");
      doc.text(packageData.businessType || "N/A", col2 + 35, yPos + 20);
      
      doc.setFont("helvetica", "bold");
      doc.text("Start Date:", col1, yPos + 29);
      doc.setFont("helvetica", "normal");
      doc.text(formatDate(packageData.startDate) || "N/A", col1 + 25, yPos + 29);
      
      doc.setFont("helvetica", "bold");
      doc.text("End Date:", col2, yPos + 29);
      doc.setFont("helvetica", "normal");
      doc.text(formatDate(packageData.endDate) || "N/A", col2 + 25, yPos + 29);
      
      doc.setFont("helvetica", "bold");
      doc.text("Duration:", col1, yPos + 38);
      doc.setFont("helvetica", "normal");
      doc.text(`${packageData.duration || "N/A"} (${String(packageData.totalDays || 0)} days)`, col1 + 22, yPos + 38);
      
      yPos += 50;

      // Key Metrics Section - Single row of 3 boxes
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(17, 24, 39);
      doc.text("Performance Metrics", margin, yPos);
      yPos += 7;

      const boxWidth = (pageWidth - margin * 2 - 16) / 3;
      const boxHeight = 20;
      
      // Calculate total cost and impressions dynamically (same as UI)
      const screensForMetrics = enhancedScreens && enhancedScreens.length > 0 ? enhancedScreens : (packageData.screens || []);
      const totalDaysMetrics = packageData.totalDays || 1;
      const totalDailyPriceMetrics = screensForMetrics.reduce((sum, screen) => sum + (screen.perDayPrice || screen.price || 0), 0);
      const totalCostMetrics = totalDailyPriceMetrics * totalDaysMetrics;
      const totalImpressionsMetrics = screensForMetrics.reduce((sum, screen) => {
        return sum + Math.round((screen.impressions || 0) * totalDaysMetrics);
      }, 0);
      const totalSpotsMetrics = screensForMetrics.reduce((sum, screen) => {
        return sum + (screen.calculatedSpotsPerDay || 0) * totalDaysMetrics;
      }, 0);
      const totalScreensMetrics = screensForMetrics.reduce((sum, screen) => {
        return sum + (screen.screensCount || 1);
      }, 0);
      
      // Tax calculation for PDF metrics - 8% for Singapore
      const pdfCountry = packageData.country || '';
      const pdfIsSingapore = pdfCountry.toLowerCase() === 'singapore';
      const pdfTaxPercent = pdfIsSingapore ? 8 : 0;
      const pdfTaxValue = parseFloat((totalCostMetrics * (pdfTaxPercent / 100)).toFixed(2));
      const pdfNetTotal = parseFloat((totalCostMetrics + pdfTaxValue).toFixed(2));
      
      const metricsData = pdfIsSingapore
        ? [
            { label: "Media Cost (Subtotal)", value: String(formatCurrency(totalCostMetrics)), bg: [16, 185, 129] },
            { label: `Tax 8% (Singapore)`, value: String(formatCurrency(pdfTaxValue)), bg: [168, 85, 247] },
            { label: "Total Value", value: String(formatCurrency(pdfNetTotal)), bg: [34, 197, 94] },
            { label: "Billboards", value: String(packageData.totalScreens || 0), bg: [59, 130, 246] },
            { label: "Impressions", value: String(totalImpressionsMetrics.toLocaleString()), bg: [139, 92, 246] }
          ]
        : [
            { label: "Total Cost", value: String(formatCurrency(totalCostMetrics)), bg: [16, 185, 129] },
            { label: "Billboards", value: String(packageData.totalScreens || 0), bg: [59, 130, 246] },
            { label: "Impressions", value: String(totalImpressionsMetrics.toLocaleString()), bg: [139, 92, 246] },
            { label: "Total Spots", value: totalSpotsMetrics > 0 ? String(Math.round(totalSpotsMetrics).toLocaleString()) : 'N/A', bg: [236, 72, 153] },
            { label: "Total Screens", value: String(totalScreensMetrics), bg: [245, 158, 11] }
          ];
      
      // Render metrics in 2 rows (3 in first row, 2 in second row)
      metricsData.forEach((metric, i) => {
        const row = Math.floor(i / 3);
        const col = i % 3;
        const x = margin + col * (boxWidth + 8);
        const y = yPos + row * (boxHeight + 4);
        
        doc.setFillColor(metric.bg[0], metric.bg[1], metric.bg[2]);
        doc.roundedRect(x, y, boxWidth, boxHeight, 2, 2, "F");
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.text(metric.label, x + 4, y + 7);
        
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text(String(metric.value), x + 4, y + 15);
      });
      
      yPos += boxHeight * 2 + 8;

      // Selected Billboards Section
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(17, 24, 39);
      doc.text(`Selected Billboards (${String(packageData.screens?.length || 0)})`, margin, yPos);
      yPos += 6;

      // Table header
      doc.setFillColor(243, 244, 246);
      doc.rect(margin, yPos, pageWidth - margin * 2, 10, "F");
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(75, 85, 99);
      doc.text("#", margin + 4, yPos + 7);
      doc.text("Billboard Name", margin + 12, yPos + 7);
      doc.text("Location", margin + 58, yPos + 7);
      doc.text("Type", margin + 85, yPos + 7);
      doc.text("Resolution", margin + 105, yPos + 7);
      doc.text("Spots/Day", margin + 133, yPos + 7);
      doc.text("Price", pageWidth - margin - 3, yPos + 7, { align: "right" });
      yPos += 12;

      // Use enhanced screens if available, otherwise fall back to packageData.screens
      const screens = enhancedScreens && enhancedScreens.length > 0 ? enhancedScreens : (packageData.screens || []);
      for (let i = 0; i < screens.length; i++) {
        const s = screens[i];
        
        if (yPos > pageHeight - 55) {
          doc.addPage();
          yPos = 20;
          
          // Repeat table header on new page
          doc.setFillColor(243, 244, 246);
          doc.rect(margin, yPos, pageWidth - margin * 2, 10, "F");
          doc.setFontSize(7);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(75, 85, 99);
          doc.text("#", margin + 4, yPos + 7);
          doc.text("Billboard Name", margin + 12, yPos + 7);
          doc.text("Location", margin + 58, yPos + 7);
          doc.text("Type", margin + 85, yPos + 7);
          doc.text("Resolution", margin + 105, yPos + 7);
          doc.text("Spots/Day", margin + 133, yPos + 7);
          doc.text("Price", pageWidth - margin - 3, yPos + 7, { align: "right" });
          yPos += 12;
        }
        
        // Alternating row background
        if (i % 2 === 0) {
          doc.setFillColor(249, 250, 251);
          doc.rect(margin, yPos - 3, pageWidth - margin * 2, 12, "F");
        }
        
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(17, 24, 39);
        
        doc.text(String(i + 1), margin + 4, yPos + 5);
        
        const displayName = String(s.displayName || "Billboard");
        doc.text(displayName.length > 22 ? displayName.substring(0, 22) + "..." : displayName, margin + 12, yPos + 5);
        
        const location = String(s.location || "N/A");
        doc.text(location.length > 13 ? location.substring(0, 13) + "..." : location, margin + 58, yPos + 5);
        
        doc.text(String(s.screenType || "Digital"), margin + 85, yPos + 5);
        
        // Display resolution from resolution1Width and resolution1Height
        const resolution = (s.resolution1Width && s.resolution1Height) 
          ? `${s.resolution1Width} X ${s.resolution1Height}`
          : String(s.size || s.resolution || "—");
        doc.text(resolution.length > 12 ? resolution.substring(0, 12) : resolution, margin + 105, yPos + 5);
        
        // Display spots per day
        const spotsPerDay = s.calculatedSpotsPerDay ? Math.round(s.calculatedSpotsPerDay).toLocaleString() : 'N/A';
        doc.text(String(spotsPerDay), margin + 133, yPos + 5);
        
        doc.setFont("helvetica", "bold");
        doc.setTextColor(16, 185, 129);
        const screenPrice = (s.perDayPrice || s.price || 0) * (packageData.totalDays || 1);
        doc.text(String(formatCurrency(screenPrice)), pageWidth - margin - 3, yPos + 5, { align: "right" });
        
        yPos += 12;
      }

      // Summary section
      yPos += 8;
      if (yPos > pageHeight - 50) {
        doc.addPage();
        yPos = 25;
      }

      // Calculate tax for summary section
      const summaryCountry = packageData.country || '';
      const summaryIsSingapore = summaryCountry.toLowerCase() === 'singapore';
      const summaryTaxPercent = summaryIsSingapore ? 8 : 0;
      const summaryTaxValue = parseFloat((totalCostMetrics * (summaryTaxPercent / 100)).toFixed(2));
      const summaryNetTotal = parseFloat((totalCostMetrics + summaryTaxValue).toFixed(2));
      
      // Draw box with appropriate height based on country
      const summaryBoxHeight = summaryIsSingapore ? 40 : 28;
      doc.setFillColor(243, 244, 246);
      doc.setDrawColor(229, 231, 235);
      doc.roundedRect(margin, yPos, pageWidth - margin * 2, summaryBoxHeight, 3, 3, "FD");
      
      // Add header text
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(17, 24, 39);
      doc.text("Campaign Summary", margin + 8, yPos + 8);
      
      const summaryCol1 = margin + 8;
      const valueX = pageWidth - margin - 8;
      
      if (summaryIsSingapore) {
        // Show three-line breakdown for Singapore
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(75, 85, 99);
        doc.text("Media Cost (Subtotal):", summaryCol1, yPos + 16);
        doc.text(String(formatCurrency(totalCostMetrics)), valueX, yPos + 16, { align: "right" });
        
        doc.text("Tax 8% :", summaryCol1, yPos + 23);
        doc.setTextColor(168, 85, 247);
        doc.text(String(formatCurrency(summaryTaxValue)), valueX, yPos + 23, { align: "right" });
        
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(16, 185, 129);
        doc.text("Total Value:", summaryCol1, yPos + 31);
        doc.setFontSize(11);
        doc.text(String(formatCurrency(summaryNetTotal)), valueX, yPos + 31, { align: "right" });
        yPos += 42;
      } else {
        // Original single-line total for non-Singapore
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(16, 185, 129);
        doc.text("Total:", summaryCol1, yPos + 18);
        doc.setFontSize(11);
        doc.text(String(formatCurrency(totalCostMetrics)), valueX, yPos + 18, { align: "right" });
        yPos += 30;
      }

      // Terms and conditions note
      yPos += 6;
      doc.setFontSize(7);
      doc.setTextColor(156, 163, 175);
      doc.setFont("helvetica", "italic");
      doc.text("* Prices are estimates and subject to final confirmation. Impressions and reach are projected based on industry data.", margin, yPos);
      doc.text("* Actual performance may vary based on placement, timing, and market conditions.", margin, yPos + 8);

      // Footer
      yPos = pageHeight - 12;
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.3);
      doc.line(margin, yPos - 5, pageWidth - margin, yPos - 5);
      
      doc.setFontSize(7);
      doc.setTextColor(156, 163, 175);
      doc.setFont("helvetica", "normal");
      doc.text("Generated by Moving Walls Marketplace AI Campaign Builder", margin, yPos);
      doc.text(`Page 1 of ${String(doc.getNumberOfPages())}`, pageWidth - margin, yPos, { align: "right" });

      // Update page numbers on all pages
      const totalPages = doc.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFontSize(7);
        doc.setTextColor(156, 163, 175);
        doc.text(`Page ${String(p)} of ${String(totalPages)}`, pageWidth - margin, pageHeight - 12, { align: "right" });
      }

      doc.save(`Campaign_${packageData.packageId}.pdf`);

      toast({
        title: "Download Complete",
        description: "Your campaign summary PDF has been downloaded."
      });
    } catch (error) {
      logger.error("PDF generation error:", error);
      toast({
        title: "Download Failed",
        description: "Unable to generate the PDF. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleStartOver = () => {
    sessionStorage.removeItem("pendingPackage");
    sessionStorage.removeItem("aiCampaignData");
    sessionStorage.removeItem("aiSessionId");
    navigate("/");
  };

  const handleSubmitLead = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!leadForm.name || !leadForm.email || !leadForm.phone || !leadForm.company || !leadForm.address) {
      toast({
        title: "Required Fields",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(leadForm.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }

    // Check if we have billboards with countryId
    if (!enhancedScreens || enhancedScreens.length === 0) {
      toast({
        title: "No Billboards",
        description: "Please wait for billboard data to load.",
        variant: "destructive"
      });
      return;
    }

    // Get countryId from first enhanced screen
    const countryId = enhancedScreens[0]?.countryId || packageData?.countryId;
    if (!countryId) {
      toast({
        title: "Missing Country Data",
        description: "Unable to determine country. Please try again.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Map enhancedScreens to addedBillboardIds format
      const addedBillboardIds = enhancedScreens.map(screen => ({
        billboardId: screen.id || screen.inventoryId.toString(),
        billboardType: (screen.screenType || 'digital').toLowerCase()
      }));

      // Prepare mutation variables
      const variables = {
        name: leadForm.name,
        companyName: leadForm.company,
        email: leadForm.email,
        phone: leadForm.phone,
        address: leadForm.address,
        startDate: packageData?.startDate || new Date().toISOString().split('T')[0],
        endDate: packageData?.endDate || new Date().toISOString().split('T')[0],
        addedBillboardIds,
        companyId: import.meta.env.VITE_APP_COMPANY_ID,
        countryId,
        countryName: packageData?.country || '',
        exposure: import.meta.env.VITE_APP_EXPOSURE || 'MARKETPLACE'
      };

      logger.log('Submitting enquiry with variables:', variables);

      const { data } = await submitEnquiry({ variables });

      if (data?.submitEnquiry?.message) {
        toast({
          title: "Request Submitted!",
          description: data.submitEnquiry.message || "Successfully created enquiry",
        });
        setShowLeadModal(false);
        // Reset form
        setLeadForm({
          name: "",
          email: "",
          phone: "",
          company: "",
          address: ""
        });
        sessionStorage.removeItem("pendingPackage");
        sessionStorage.removeItem("aiCampaignData");
        sessionStorage.removeItem("aiSessionId");
        navigate("/?success=true");
      } else {
        throw new Error("Failed to submit enquiry");
      }
    } catch (error: any) {
      logger.error("Enquiry submission error:", error);
      toast({
        title: "Submission Failed",
        description: error.message || "Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to generate campaign name
  const generateCampaignName = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `AI Campaign - MW-${result}`;
  };

  const handleCreateProposal = async () => {
    if (isAuthLoading) {
      return;
    }
    
    if (!isAuthenticated) {
      // Pre-fill address for non-authenticated users
      setLeadForm(prev => ({
        ...prev,
        address: packageData?.location && packageData?.country 
          ? `${packageData.location}, ${packageData.country}` 
          : ''
      }));
      setShowLeadModal(true);
      return;
    }

    if (!packageData || !enhancedScreens || enhancedScreens.length === 0) {
      toast({
        title: "Package Data Missing",
        description: "Please wait for package data to load.",
        variant: "destructive"
      });
      return;
    }

    setIsCreatingProposal(true);
    try {
      // Get access token
      const accessToken = localStorage.getItem("authToken");
      if (!accessToken) {
        throw new Error("No access token found. Please login again.");
      }
      
      // Use enhanced screens if available, otherwise fall back to package screens
      const screensToUse = enhancedScreens && enhancedScreens.length > 0 ? enhancedScreens : packageData.screens;
      const totalDays = packageData.totalDays || 1;
      
      // Calculate totals
      const totalDailyPrice = screensToUse.reduce((sum, screen) => sum + (screen.perDayPrice || screen.price || 0), 0);
      const totalCost = parseFloat((totalDailyPrice * totalDays).toFixed(2));
      const totalImpressions = screensToUse.reduce((sum, screen) => {
        return sum + Math.round((screen.impressions || 0) / 30 * totalDays);
      }, 0);
      const totalReach = screensToUse.reduce((sum, screen) => sum + (screen.totalVisitors || 0), 0);
      
      const currencyCode = (enhancedScreens[0]?.currency?.code) || packageData.currency?.code || 'INR';
      const currencySymbol = (enhancedScreens[0]?.currency?.symbol) || packageData.currency?.symbol || currencyCode;
      const currencyCountry = (enhancedScreens[0]?.currency?.country) || packageData.currency?.name || packageData.country || '';
      
      // Calculate inventories summary
      const digitalCount = screensToUse.filter(s => s.screenType?.toLowerCase() === 'digital').length;
      const classicCount = screensToUse.filter(s => s.screenType?.toLowerCase() === 'classic' || s.screenType?.toLowerCase() === 'static').length;
      
      // Tax calculation - 8% for Singapore, 0% for other countries
      const country = packageData.country || currencyCountry || '';
      const isSingapore = country.toLowerCase() === 'singapore';
      const taxPercent = isSingapore ? 8 : 0;
      const taxLabel = 'Tax';
      const taxValue = parseFloat((totalCost * (taxPercent / 100)).toFixed(2));
      const netTotal = parseFloat((totalCost + taxValue).toFixed(2));
      
      // Calculate CPM and average frequency for summary
      const summaryReportCpm = totalImpressions > 0 ? parseFloat((totalCost / (totalImpressions / 1000)).toFixed(2)) : 0;
      const summaryReportAvgFreq = totalReach > 0 ? parseFloat((totalImpressions / totalReach).toFixed(2)) : 1;
      
      // Generate campaign name
      const campaignName = generateCampaignName();
      
      // Get user data from localStorage
      const userAccountData = localStorage.getItem('userAccountData');
      const storedUserData = userAccountData ? JSON.parse(userAccountData) : null;
      
      // Get user and company IDs
      logger.log('Stored user account data:', storedUserData);
      
      const userId = storedUserData[0]?.id || '';
      const companyId = import.meta.env.VITE_APP_COMPANY_ID || '';
      const agencyId = storedUserData[0]?.companyId || '';
      const agencyName = storedUserData[0]?.companyName || '';
      const companyName = storedUserData[0]?.companyName || '';
      const userName = storedUserData[0]?.login || '';
      
      // Map screens to campaign inventories
      const campaignInventories = screensToUse.map((screen) => {
        const impressions = Math.round((screen.impressions || 0) / 30 * totalDays);
        const reach = screen.totalVisitors || 0;
        const price = parseFloat(((screen.perDayPrice || screen.price || 0) * totalDays).toFixed(2));
        const cpm = impressions > 0 ? parseFloat((price / (impressions / 1000)).toFixed(2)) : 0;
        
        return {
          dsp: agencyName,
          startDate: packageData.startDate,
          endDate: packageData.endDate,
          companyId: companyId,
          inventoryPrice: price,
          inventoryName: screen.displayName || 'Billboard',
          inventoryId: screen.id || screen.inventoryId.toString(),
          inventoryReferenceId: screen.referenceId || screen.inventoryId.toString(),
          inventoryType: screen.screenType?.toLowerCase() || 'digital',
          inventoryAddress: screen.location || '',
          inventoryVenueType: screen.venueType || '',
          inventoryResolution: {
            width: parseFloat(screen.resolution1Width || screen.resolution?.split(/\s*[xX]\s*/)[0] || '0'),
            height: parseFloat(screen.resolution1Height || screen.resolution?.split(/\s*[xX]\s*/)[1] || '0')
          },
          inventoryThumbnailUrl: screen.assetUrl || '',
          inventoryLatitude: screen.latitude || 0,
          inventoryLongitude: screen.longitude || 0,
          inventoryReports: {
            totalPotentialViews: impressions,
            averageFrequency: 1,
            cpm: cpm,
            totalReach: reach,
          },
          isSpotBased: screen.calculatedSpotsPerDay ? true : (screen.spots ? true : false),
          spotsPerDay: screen.calculatedSpotsPerDay ? Math.round(screen.calculatedSpotsPerDay) : (screen.spots ? Math.round(screen.spots / screen.days!) : null),
          spotDuration: screen.spotDuration || 15,
        };
      });
      
      // Prepare mutation variables matching the working payload format
      const approvalVariables = {
        campaignType: "create-campaign",
        campaignId: "",
        input: {
          accessToken: accessToken,
          name: campaignName,
          bookingSource: "ONLINE",
          startDate: packageData.startDate,
          endDate: packageData.endDate,
          campaignStatus: "REQUESTED",
          agency: {
            id: agencyId || companyId,
            name: agencyName
          },
          company: {
            id: companyId,
            name: companyName
          },
          user: {
            id: userId,
            name: userName
          },
          platformSource: "MARKETPLACE",
          dsp: agencyName,
          inventoriesSummary: {
            classicInventories: classicCount,
            digitalInventories: digitalCount,
            packages: 0,
          },
          priceSummary: {
            grossTotal: totalCost,
            subTotal: totalCost,
            netTotal: netTotal,
            tax: {
              name: taxLabel,
              value: taxValue,
              percent: taxPercent,
            },
            currency: {
              country: currencyCountry,
              symbol: currencySymbol,
              code: currencyCode,
            },
            discountedPrice: 0,
            discountPercent: 0,
            couponName: "",
          },
          summaryReport: {
            potentialViews: totalImpressions,
            uniqueReach: totalReach,
            cpm: summaryReportCpm,
            averageFrequency: summaryReportAvgFreq,
            totalSpots: screensToUse.length,
          },
          reporting: {
            proofOfPlay: true,
            deliveryReports: true,
          },
          campaignInventories: campaignInventories
        },
      };
      
      logger.log('Submitting REQUEST_FOR_APPROVAL with variables:', approvalVariables);
      
      const approvalResponse = await client.mutate({
        mutation: REQUEST_FOR_APPROVAL,
        variables: approvalVariables,
        fetchPolicy: "no-cache",
      });
      
      logger.log('REQUEST_FOR_APPROVAL response:', approvalResponse);
      
      if (approvalResponse?.data?.requestForApproval?.campaign) {
        toast({
          title: "Proposal Created!",
          description: "Your campaign proposal has been created and is pending approval."
        });
        
        // Clean up session storage
        sessionStorage.removeItem("pendingPackage");
        sessionStorage.removeItem("aiCampaignData");
        sessionStorage.removeItem("aiSessionId");
        
        // Navigate to campaigns page
        const base_path = import.meta.env.VITE_APP_BASE_PATH || '';
        navigate(`${base_path}/my-campaigns`);
      } else {
        throw new Error("Failed to create proposal - unexpected response");
      }
    } catch (error: any) {
      logger.error("Proposal creation error:", error);
      toast({
        title: "Failed to Create Proposal",
        description: error.message || "Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCreatingProposal(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your package...</p>
        </div>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2 text-gray-900">Package Not Found</h1>
          <p className="text-gray-600 mb-6">The package data could not be loaded.</p>
          <Button onClick={() => navigate("/")} className="bg-emerald-600 hover:bg-emerald-700">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="hover:bg-gray-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-bold text-lg text-gray-900">Review Your Campaign</h1>
              <p className="text-sm text-gray-500">Review the billboards and request your campaign</p>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Monitor className="h-5 w-5 text-emerald-600" />
                  Recommended Billboards ({packageData?.screens?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {enhancedScreens && enhancedScreens.length > 0 ? (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {enhancedScreens.map((screen, index) => (
                      <ItemCard key={screen.id || screen.inventoryId || index} item={screen} index={index} startDate={packageData?.startDate} endDate={packageData?.endDate} totalDays={packageData?.totalDays} />
                    ))}
                  </div>
                ) : packageData?.screens && packageData.screens.length > 0 ? (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {packageData.screens.map((screen, index) => (
                      <ItemCard key={screen.id || screen.inventoryId || index} item={{ ...screen, isLoading: true }} index={index} startDate={packageData.startDate} endDate={packageData.endDate} totalDays={packageData?.totalDays} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Monitor className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No billboards found matching your criteria.</p>
                    <p className="text-sm mt-2">Try adjusting your campaign parameters.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card className="sticky top-24 overflow-hidden border-0 shadow-lg">
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-5">
                <h3 className="flex items-center gap-2 text-lg font-semibold">
                  <Sparkles className="h-5 w-5" />
                  Package Summary
                </h3>
              </div>

              <CardContent className="pt-4 space-y-4">
                {(() => {
                  // Calculate currency and country for UI metrics
                  const currencyCode = (enhancedScreens && enhancedScreens.length > 0 && enhancedScreens[0]?.currency?.code) || packageData.currency?.code || 'INR';
                  const uiCountry = packageData.country || '';
                  const uiIsSingapore = uiCountry.toLowerCase() === 'singapore';
                  
                  // Calculate costs
                  const totalDailyPrice = enhancedScreens && enhancedScreens.length > 0 
                    ? enhancedScreens.reduce((sum, screen) => sum + (screen.perDayPrice || screen.price || 0), 0)
                    : (packageData.screens || []).reduce((sum, screen) => sum + (screen.price || 0), 0);
                  const totalDays = packageData.totalDays || 1;
                  const totalCost = totalDailyPrice * totalDays;
                  const uiTaxValue = uiIsSingapore ? parseFloat((totalCost * 0.08).toFixed(2)) : 0;
                  const uiNetTotal = uiIsSingapore ? parseFloat((totalCost + uiTaxValue).toFixed(2)) : totalCost;
                  
                  return (
                    <div className="grid grid-cols-2 gap-3">
                      {uiIsSingapore ? (
                        <>
                          <MetricBox
                            label="Media Cost"
                            value={`${currencyCode} ${totalCost.toLocaleString()}`}
                            icon={DollarSign}
                          />
                          <MetricBox
                            label="Tax 8%"
                            value={`${currencyCode} ${uiTaxValue.toLocaleString()}`}
                            icon={DollarSign}
                          />
                          <MetricBox
                            label="Total Value"
                            value={`${currencyCode} ${uiNetTotal.toLocaleString()}`}
                            icon={DollarSign}
                          />
                          <MetricBox
                            label="Billboards"
                            value={packageData.totalScreens || 0}
                            icon={Building2}
                          />
                          <MetricBox
                            label="Days"
                            value={packageData.totalDays || 0}
                            icon={Calendar}
                          />
                          <MetricBox
                            label="Total Impressions"
                            value={(() => {
                              const screensToUse = enhancedScreens && enhancedScreens.length > 0 ? enhancedScreens : (packageData.screens || []);
                              const totalImp = screensToUse.reduce((sum, screen) => {
                                return sum + Math.round((screen.impressions || 0) * totalDays);
                              }, 0);
                              return totalImp.toLocaleString();
                            })()}
                            icon={Eye}
                          />
                        </>
                      ) : (
                        <>
                          <MetricBox
                            label="Total Cost"
                            value={`${currencyCode} ${totalCost.toLocaleString()}`}
                            icon={DollarSign}
                          />
                          <MetricBox
                            label="Billboards"
                            value={packageData.totalScreens || 0}
                            icon={Building2}
                          />
                          <MetricBox
                            label="Days"
                            value={packageData.totalDays || 0}
                            icon={Calendar}
                          />
                          <MetricBox
                            label="Total Impressions"
                            value={(() => {
                              const screensToUse = enhancedScreens && enhancedScreens.length > 0 ? enhancedScreens : (packageData.screens || []);
                              const totalImp = screensToUse.reduce((sum, screen) => {
                                return sum + Math.round((screen.impressions || 0) * totalDays);
                              }, 0);
                              return totalImp.toLocaleString();
                            })()}
                            icon={Eye}
                          />
                          <MetricBox
                            label="Total Spots"
                            value={(() => {
                              const screensToUse = enhancedScreens && enhancedScreens.length > 0 ? enhancedScreens : (packageData.screens || []);
                              const totalSpots = screensToUse.reduce((sum, screen) => {
                                return sum + (screen.calculatedSpotsPerDay || 0) * totalDays;
                              }, 0);
                              return totalSpots > 0 ? Math.round(totalSpots).toLocaleString() : 'N/A';
                            })()}
                            icon={Sparkles}
                          />
                          <MetricBox
                            label="Total Screens"
                            value={(() => {
                              const screensToUse = enhancedScreens && enhancedScreens.length > 0 ? enhancedScreens : (packageData.screens || []);
                              const totalScreens = screensToUse.reduce((sum, screen) => {
                                return sum + (screen.screensCount || 1);
                              }, 0);
                              return totalScreens;
                            })()}
                            icon={Monitor}
                          />
                        </>
                      )}
                    </div>
                  );
                })()}

                <div className="text-sm text-gray-600 bg-emerald-50 p-3 rounded-lg">
                  <div className="flex items-center gap-1 mb-2 font-medium text-emerald-700">
                    <Calendar className="h-4 w-4" />
                    Campaign Details
                  </div>
                  <div className="space-y-1">
                    {packageData.location && (
                      <p className="flex items-center gap-2">
                        <MapPin className="h-3 w-3" />
                        {packageData.location}, {packageData.country}
                      </p>
                    )}
                    {packageData.duration && (
                      <p className="flex items-center gap-2">
                        <Calendar className="h-3 w-3" />
                        {packageData.duration}
                      </p>
                    )}
                    {packageData.startDate && packageData.endDate && (
                      <p className="text-xs text-gray-500">
                        {formatDate(packageData.startDate)} to {formatDate(packageData.endDate)}
                      </p>
                    )}
                    {packageData.businessType && (
                      <p className="flex items-center gap-2">
                        <Building2 className="h-3 w-3" />
                        {packageData.businessType}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2 pt-2 border-t">
                  <Button
                    onClick={handleCreateProposal}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    size="lg"
                    disabled={isCreatingProposal || isAuthLoading}
                  >
                    {(isCreatingProposal || isAuthLoading) ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    {isAuthLoading ? "Loading..." : isAuthenticated ? "Create Proposal" : "Request Package"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDownloadPDF}
                    className="w-full"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Summary
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleStartOver}
                    className="w-full text-gray-500 hover:text-gray-700"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Start Over
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Dialog open={showLeadModal} onOpenChange={setShowLeadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm font-semibold text-white">
              <Send className="h-5 w-5 text-emerald-600" />
              Request Package
            </DialogTitle>
            <DialogDescription>
              Fill in your details and we'll get back to you with a custom quote.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitLead} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="Your full name"
                value={leadForm.name}
                onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={leadForm.email}
                onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 234 567 8900"
                value={leadForm.phone}
                onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">Company *</Label>
              <Input
                id="company"
                placeholder="Your company name"
                value={leadForm.company}
                onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address *</Label>
              <Textarea
                id="address"
                placeholder="Your business address"
                value={leadForm.address}
                onChange={(e) => setLeadForm({ ...leadForm, address: e.target.value })}
                rows={2}
                required
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowLeadModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
